import React, { useEffect, useState } from "react";
import api from "../../api";
import { useNavigate, useParams } from "react-router-dom";

export default function ViewMegaScholarship() {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);

  const { id } = useParams();
  const nav = useNavigate();

  // 🔹 LOAD EXAM
  useEffect(() => {
    loadExam();
  }, [id]);

  // 🔹 LOAD SUBJECT NAMES
  useEffect(() => {
    if (exam?.subject_ids?.length) {
      api
        .get("/public/subjects", {
          params: { ids: exam.subject_ids }
        })
        .then(res => setSubjects(res.data))
        .catch(() => setSubjects([]));
    }
  }, [exam]);

  const loadExam = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/mega_scholarship/${id}`);
      const examData = res.data;

      examData.assigned_questions = Array.isArray(examData.assigned_questions)
        ? examData.assigned_questions
        : [];

      setExam(examData);
    } catch (err) {
      console.error(err);
      alert("Failed to load Exam");
      nav("/admin/mega-scholarship-list");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={center}>
        <p>Loading Exam...</p>
      </div>
    );
  }

  if (!exam) {
    return (
      <div style={center}>
        <p>Exam not found</p>
        <button onClick={() => nav("/admin/mega-scholarship-list")}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      {/* HEADER */}
      <div style={header}>
        <button onClick={() => nav("/admin/mega-scholarship-list")} style={backBtn}>
          ← Back
        </button>
        <h1>View Mega Scholarship: #{id}</h1>
      </div>

      <div style={{ padding: 40 }}>
        {/* INFO CARD */}
        <div style={card}>
          <h2>{exam.title}</h2>

          <div style={grid}>
            <Info label="Class" value={exam.class_id} />
            
            <Info label="Subjects" value={exam.subject_ids?.join(", ")} />
            <Info
              label="Questions"
              value={`${exam.assigned_questions.length}/${exam.total_questions}`}
            />
            <Info label="Duration" value={`${exam.duration_minutes} min`} />
            <Info label="Entry Fee" value={`₹${exam.entry_fee}`} />
            <Info
              label="Exam Date"
              value={new Date(exam.exam_date).toLocaleDateString("en-IN")}
            />
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ marginTop: 30, display: "flex", gap: 16 }}>
            {!exam.result_declared && (
              <button
                onClick={async () => {
                  if (!window.confirm("Declare result now?")) return;
                  try {
                    await api.post(`/admin/mega-scholarship/${exam.id}/declare-result`);
                    alert("✅ Result declared successfully");
                    loadExam();
                  } catch (err) {
                    alert(err.response?.data?.msg || "Failed to declare result");
                  }
                }}
                style={declareBtn}
              >
                📢 Declare Result
              </button>
            )}

            <button
              disabled={!exam.result_declared}
              onClick={async () => {
                if (!window.confirm("Release rewards now?")) return;
                try {
                  await api.post(`/admin/mega-scholarship/${exam.id}/release-reward`);
                  alert("🎁 Rewards unlocked successfully");
                } catch (err) {
                  alert(err.response?.data?.error || "Failed to release rewards");
                }
              }}
              style={{
                ...rewardBtn,
                background: exam.result_declared ? "#16a34a" : "#94a3b8",
                cursor: exam.result_declared ? "pointer" : "not-allowed"
              }}
            >
              🎁 Release Rewards
            </button>

            <button
              onClick={() =>
                nav(`/admin/mega-scholarship/${exam.id}/participants`)
              }
              style={{
                padding: "12px 28px",
                background: "#0f172a",
                color: "white",
                borderRadius: 10,
                fontWeight: 700
              }}
            >
              💰 Manage Rewards
            </button>
          </div>
        </div>

        {/* QUESTIONS TABLE */}
        <div style={tableWrap}>
          <div style={tableHeader}>
            Questions ({exam.assigned_questions.length})
          </div>

          <table style={table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Question</th>
                <th>A</th>
                <th>B</th>
                <th>C</th>
                <th>D</th>
                <th>Correct</th>
              </tr>
            </thead>
            <tbody>
              {exam.assigned_questions.map((q, i) => (
                <tr key={q.id}>
                  <td>{i + 1}</td>
                  <td>{q.text}</td>
                  <td>{q.option_a}</td>
                  <td>{q.option_b}</td>
                  <td>{q.option_c}</td>
                  <td>{q.option_d}</td>
                  <td>
                    <span style={correctBadge}>{q.correct}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {exam.assigned_questions.length === 0 && (
            <p style={{ padding: 30, textAlign: "center" }}>
              No questions assigned
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- SMALL COMPONENT ---------- */
const Info = ({ label, value }) => (
  <div style={infoBox}>
    <label>{label}</label>
    <p>{value}</p>
  </div>
);

/* ---------- STYLES ---------- */
const center = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center"
};

const header = {
  padding: "20px 40px",
  background: "white",
  borderBottom: "1px solid #e2e8f0",
  display: "flex",
  gap: 20,
  alignItems: "center"
};

const backBtn = {
  padding: "8px 14px",
  background: "#e2e8f0",
  border: "none",
  borderRadius: 6,
  cursor: "pointer"
};

const card = {
  background: "white",
  padding: 24,
  borderRadius: 16,
  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
  marginBottom: 30
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 20
};

const infoBox = {
  background: "#f0fdf4",
  padding: 16,
  borderRadius: 12
};

const rewardBtn = {
  padding: "12px 28px",
  color: "white",
  border: "none",
  borderRadius: 10,
  fontWeight: 700
};

const tableWrap = {
  background: "white",
  borderRadius: 16,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  overflow: "hidden"
};

const tableHeader = {
  padding: "18px 24px",
  background: "#1e293b",
  color: "white",
  fontWeight: 600
};

const table = {
  width: "100%",
  borderCollapse: "collapse"
};

const correctBadge = {
  background: "#d1fae5",
  color: "#065f46",
  padding: "6px 12px",
  borderRadius: 20
};

const declareBtn = {
  padding: "12px 28px",
  background: "#2563eb",
  color: "white",
  border: "none",
  borderRadius: 10,
  fontWeight: 700,
  cursor: "pointer"
};
